import React, { useEffect, useState } from "react";
import { api } from "../../lib/api.js";
import { toast } from "sonner";
import { Save, RefreshCw, Sliders, RotateCcw, Trash2 } from "lucide-react";
import { showAdminSaveToast } from "./_shared.jsx";

const FIELDS = [
  {
    key: "notification_retention_days",
    label: "Retenção de notificações",
    unit: "dias",
    hint: "Notificações mais antigas do que este limite são apagadas automaticamente.",
    type: "number",
  },
  {
    key: "state_dwell_interval_minutes",
    label: "Intervalo do scan de bloqueios",
    unit: "minutos",
    hint: "Frequência com que o backend verifica SOMs paradas em cada estado e dispara alertas.",
    type: "number",
  },
  {
    key: "max_excel_rows",
    label: "Máximo de linhas por importação Excel",
    unit: "linhas",
    hint: "Rejeita ficheiros Excel com mais linhas do que este limite.",
    type: "number",
  },
  {
    key: "alert_dedup_hours",
    label: "Janela anti-spam de alertas por email",
    unit: "horas",
    hint: "Um alerta com a mesma chave, registo e nível não é reenviado dentro desta janela — evita spam quando o scan corre a cada poucos minutos.",
    type: "number",
  },
  {
    key: "bps_workshop_stuck_days",
    label: "BPS: viaturas paradas em cada fase da oficina",
    unit: "dias",
    hint: "Threshold do card de excepção #18 no painel — viaturas paradas há mais do que este número de dias na mesma fase da pipeline oficina.",
    type: "number",
  },
  {
    key: "peb_no_contact_hours",
    label: "PEB: recebidas sem contactar cliente",
    unit: "horas",
    hint: "Threshold do card de excepção #19 — PEBs recebidas ou prontas para levantamento sem contactar o cliente.",
    type: "number",
  },
  {
    key: "peb_customs_stuck_days",
    label: "PEB: presas nas alfândegas",
    unit: "dias",
    hint: "Threshold do card de excepção #20 — PEBs em trânsito e paradas nas alfândegas.",
    type: "number",
  },
  {
    key: "nps_benchmark_threshold_global",
    label: "Alerta NPS Benchmark · global",
    unit: "pontos",
    hint: "Queda em pontos (mês actual vs anterior) acima da qual é enviado alerta global aos managers. Default: 10.",
    type: "number",
  },
  {
    key: "nps_benchmark_threshold_vendas",
    label: "Alerta NPS · Vendas",
    unit: "pontos",
    hint: "Threshold específico para Vendas.",
    type: "number",
  },
  {
    key: "nps_benchmark_threshold_workshop",
    label: "Alerta NPS · Workshop",
    unit: "pontos",
    hint: "Threshold específico para a oficina.",
    type: "number",
  },
  {
    key: "nps_benchmark_threshold_bps",
    label: "Alerta NPS · BPS",
    unit: "pontos",
    hint: "Threshold específico para bate-chapas.",
    type: "number",
  },
  {
    key: "nps_benchmark_threshold_reclamacoes",
    label: "Alerta NPS · Reclamações",
    unit: "pontos",
    hint: "Threshold específico para reclamações.",
    type: "number",
  },
  {
    key: "nps_benchmark_threshold_pecas",
    label: "Alerta NPS · Peças",
    unit: "pontos",
    hint: "Threshold específico para peças.",
    type: "number",
  },
  {
    key: "timezone",
    label: "Fuso horário do sistema",
    unit: "",
    hint: "Fuso horário base usado nas datas dos digests e formatação. Exemplo: Africa/Maputo, Europe/Lisbon, UTC.",
    type: "text",
  },
];

export default function SystemSettingsGlobalPanel() {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/system-settings");
      setCfg(data.settings);
      setDefaults(data.defaults);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar definições globais");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (k, v) => {
    setCfg((c) => ({ ...c, [k]: v }));
    setDirty(true);
  };

  const resetDefaults = () => {
    if (!defaults) return;
    setCfg({ ...defaults });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/system-settings", { settings: cfg });
      setCfg(data.settings);
      setDirty(false);
      showAdminSaveToast({ savedLabel: "Definições guardadas", warnings: data.warnings || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="p-4 text-sm text-[#6C757D]">{busy ? "A carregar…" : "Sem dados"}</div>;

  return (
    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="system-global-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-heading text-sm font-bold text-[#1A1A2E] flex items-center gap-1.5">
          <Sliders className="w-4 h-4 text-[#1F3864]" /> Definições globais
        </h3>
        <div className="flex gap-2">
          <button
            onClick={resetDefaults}
            disabled={busy || saving}
            className="px-2.5 py-1.5 text-xs rounded-sm border border-gray-300 hover:border-[#1F3864] bg-white flex items-center gap-1.5"
            data-testid="global-reset"
          >
            <RotateCcw className="w-3 h-3" /> Repor default
          </button>
          <button
            onClick={load}
            disabled={busy || saving}
            className="px-2.5 py-1.5 text-xs rounded-sm border border-gray-300 hover:border-[#1F3864] bg-white flex items-center gap-1.5"
            data-testid="global-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Recarregar
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="px-3 py-1.5 text-sm rounded-sm bg-[#1F3864] text-white hover:bg-[#142542] disabled:opacity-60 flex items-center gap-1.5"
            data-testid="global-save"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "A guardar…" : "Guardar"}
          </button>
        </div>
      </div>
      <div className="space-y-3">
        {FIELDS.map((f) => (
          <div key={f.key} className="grid md:grid-cols-3 gap-2 items-start">
            <div>
              <label className="block text-sm font-semibold text-[#1A1A2E]">{f.label}</label>
              <div className="text-[11px] text-[#6C757D]">{f.hint}</div>
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <input
                type={f.type}
                min={f.type === "number" ? "1" : undefined}
                value={cfg[f.key] ?? ""}
                onChange={(e) => update(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)}
                className="w-56 px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:outline-none focus:border-[#1F3864] font-mono"
                data-testid={`global-input-${f.key}`}
              />
              {f.unit && <span className="text-xs text-[#6C757D]">{f.unit}</span>}
              {defaults && cfg[f.key] !== defaults[f.key] && (
                <span className="text-[10px] px-1.5 py-0.5 bg-[#FFF7E6] text-[#856404] rounded-sm">alterado do default ({defaults[f.key]})</span>
              )}
            </div>
          </div>
        ))}
      </div>
      <NotificationsCleanupRow />
    </div>
  );
}

function NotificationsCleanupRow() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (dry) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/cleanup-notifications${dry ? "?dry_run=true" : ""}`);
      setResult(data);
      if (dry) {
        toast.info(`${data.matched} notificação(ões) mais antigas do que ${data.retention_days} dias — não apagado (dry-run)`);
      } else {
        toast.success(`${data.deleted} notificações apagadas`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao purgar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 pt-4 border-t border-gray-100" data-testid="notif-cleanup-row">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-[#1A1A2E]">Purga manual de notificações antigas</div>
          <div className="text-[11px] text-[#6C757D]">Executa agora o cleanup usando o valor actual de <code>notification_retention_days</code>.</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => run(true)}
            disabled={busy}
            className="px-2.5 py-1.5 text-xs rounded-sm border border-gray-300 hover:border-[#1F3864] bg-white flex items-center gap-1.5"
            data-testid="notif-cleanup-dryrun"
          >
            Simular
          </button>
          <button
            onClick={() => run(false)}
            disabled={busy}
            className="px-2.5 py-1.5 text-xs rounded-sm bg-[#DC3545] text-white hover:bg-[#a82733] disabled:opacity-60 flex items-center gap-1.5"
            data-testid="notif-cleanup-run"
          >
            <Trash2 className="w-3 h-3" /> Purgar agora
          </button>
        </div>
      </div>
      {result && (
        <div className="mt-2 text-[11px] text-[#6C757D] font-mono">
          retenção: {result.retention_days}d · corte: {result.cutoff.slice(0, 16)} · match: {result.matched} · apagadas: {result.deleted}
        </div>
      )}
    </div>
  );
}
